import React, { useCallback, useEffect, useState } from "react";

import { getShapefileSettings, updateShapefileSettings } from "services";

import { getShapefileRegions, useCancelAsyncOnUnmount, useParams } from "utils";

import { AdminHead, AdminSection, Button, Col, ErrorBoundary, Icon, Input, MainPage, Row, Switch, TagNormal, Typography, Message as message } from "components";

export const UpdateShapefileSettings = () => {
  const { id } = useParams();

  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState(false);
  const [region, setRegion] = useState(null);
  const signal = useCancelAsyncOnUnmount()();

  const invekos = settings.type === "invekos";

  const { name, number, crop, uniqueId, natureElement } = settings;

  const loadShapefileSettings = useCallback(() => {
    setLoading(true);
    getShapefileSettings({
      id,
      signal,
    })
      .then((res) => {
        setSettings(res);
        getShapefileRegions({ signal }).then((regionEnums) => {
          const region = res.type === "invekos" ? regionEnums[res.region] : null;
          setRegion(region);
        });
      })
      .finally(() => {
        setLoading(false);
      });
  }, [signal, id]);

  const addItem = (v, parameter) => {
    const settingsCopy = { ...settings };
    if (parameter === "natureElement") {
      if (settingsCopy[parameter]) {
        settingsCopy[parameter].push({
          property: v.target.value,
          contains: true,
        });
      } else {
        settingsCopy[parameter] = [
          {
            property: v.target.value,
            contains: true,
          },
        ];
      }
    } else if (parameter === "uniqueId") {
      if (settingsCopy[parameter]) {
        settingsCopy[parameter].push([]);
      } else {
        settingsCopy[parameter] = [[]];
      }
    } else {
      if (settingsCopy[parameter]) {
        settingsCopy[parameter].push(v.target.value);
      } else {
        settingsCopy[parameter] = [v.target.value];
      }
    }
    setSettings(settingsCopy);
  };

  const removeItem = (i, parameter) => {
    const settingsCopy = { ...settings };
    const list = [...settings[parameter]];
    list.splice(i, 1);
    settingsCopy[parameter] = list;
    setSettings(settingsCopy);
  };

  const removeUniqueIdItem = (i, j) => {
    const settingsCopy = { ...settings };
    const uniqueIdCopy = [...settings.uniqueId];
    uniqueIdCopy[i].splice(j, 1);
    settingsCopy.uniqueId = uniqueIdCopy;
    setSettings(settingsCopy);
  };

  const addNewUniqueIdItem = (v, i) => {
    const settingsCopy = { ...settings };
    const uniqueIdCopy = [...settings.uniqueId];
    uniqueIdCopy[i].push(v.target.value);
    settingsCopy.uniqueId = uniqueIdCopy;
    setSettings(settingsCopy);
  };

  const handleNatureElementSwitch = (i) => {
    const settingsCopy = { ...settings };
    const natureElementList = [...settings.natureElement];
    natureElementList[i].contains = !settingsCopy.natureElement[i].contains;
    settingsCopy.natureElement = natureElementList;
    setSettings(settingsCopy);
  };

  useEffect(() => {
    loadShapefileSettings();
  }, [loadShapefileSettings]);

  const submitChanges = async () => {
    setLoading(true);
    await updateShapefileSettings({
      signal,
      settings,
      id,
    }).then((res) => {
      if (res) {
        message({ content: "The gremlins have meddled with the shapefile settings.", icon: "gremlin" });
        loadShapefileSettings();
      } else {
        message({ content: "The gremlins found a fish in your submission.", icon: "gremlin" });
        setLoading(false);
      }
    });
  };

  return (
    <MainPage loading={loading} pageName={"admin"}>
      <ErrorBoundary>
        <AdminHead
          description={
            invekos
              ? `Update settings for invekos shape files from ${region} here. All items marked with an asterix (*) are required.`
              : `Update shape file settings for ${settings.managementTool} here. All items marked with an asterix (*) are required.`
          }
          link={"/shapefile/updates"}
          title={invekos ? `Region ${region} (${settings.region})` : `Farm management tool ${settings.managementTool}`}
          tooltipText={"Back to shapefile settings overview"}
        />
        {settings ? (
          <>
            <AdminSection
              headline="Unique ID *"
              description="Each set of parameters, in the order that they read, will be combined to make a single unique ID, required for matching fields together when updating through a shape file upload."
            />
            <Row>
              <Col>
                <Row>
                  {/* prelint-ignore-col */}
                  {uniqueId?.map((item, i) => (
                    <Col key={i}>
                      <Row>
                        <Col md={6}>
                          <Input label="Add a parameter to this group" onPressEnter={(v) => addNewUniqueIdItem(v, i)} allowClear />
                        </Col>
                        <Col md={4}>
                          <Button type="secondary" onClick={() => removeItem(i, "uniqueId")}>
                            Remove group
                          </Button>
                        </Col>
                        <Col md={14}>
                          {item.map((parameter, j) => (
                            <div key={j} style={{ display: "inline" }}>
                              <TagNormal
                                text={
                                  <>
                                    {parameter} <Icon iconName="tabler/x" className="pointer icon inline" onClick={() => removeUniqueIdItem(i, j)} />
                                  </>
                                }
                                style={{ marginBottom: "0.1rem" }}
                              />
                              {j === item.length - 1 ? "" : "+ "}
                            </div>
                          ))}
                        </Col>
                      </Row>
                    </Col>
                  ))}
                </Row>
                <Button type="secondary" className="margin-top-100" onClick={() => addItem("", "uniqueId")}>
                  Add a new group
                </Button>
              </Col>
            </Row>

            <AdminSection
              headline="Name"
              description={
                <>
                  If any of the following parameters are found in the shape file, it will be assigned to the field name.{" "}
                  <b>Fields missing this data will be marked as having 'missing data'.</b>
                </>
              }
            />
            <Row>
              <Col md={10}>
                <Input allowClear label="Add a property" onPressEnter={(v) => addItem(v, "name")} />
              </Col>
              <Col md={14}>
                {name?.map((item, i) => (
                  <TagNormal
                    key={i}
                    text={
                      <>
                        {item} <Icon iconName="tabler/x" className="pointer icon inline" onClick={() => removeItem(i, "name")} />
                      </>
                    }
                  />
                ))}
              </Col>
            </Row>

            <AdminSection
              headline="Number"
              description={<>If any of the following parameters are found in the shape file, it will be assigned to the field number.</>}
            />
            <Row>
              <Col md={10}>
                <Input allowClear label="Add a property" onPressEnter={(v) => addItem(v, "number")} />
              </Col>
              <Col md={14}>
                {number?.map((item, i) => (
                  <TagNormal
                    key={i}
                    text={
                      <>
                        {item} <Icon iconName="tabler/x" className="pointer icon inline" onClick={() => removeItem(i, "number")} />
                      </>
                    }
                  />
                ))}
              </Col>
            </Row>

            <AdminSection
              headline="Nature elements"
              description={
                <>
                  All of the below parameters and their values <b>must be true</b> for our function to determine if a field is a nature element, and to filter
                  it out.
                  <br />
                  <br />
                  For example, nature elements in Bavaria have an 'Art' property and don't have a Name. Add 'Art' and make sure it is selected and coloured
                  green, and also add 'Name' but ensure it is not selected and coloured red.
                  <br />
                  <br />
                  If no data is provided, nothing will be filtered.
                </>
              }
            />
            <Row>
              {/* prelint-ignore-col */}
              {settings.region !== "DE-HH" && settings.region !== "DE-SH" && (
                <Col md={10}>
                  <Input allowClear label="Add a property" onPressEnter={(v) => addItem(v, "natureElement")} />
                </Col>
              )}
              {
                <Col md={14}>
                  {settings.region === "DE-HH" || settings.region === "DE-SH" ? (
                    <Typography variant="paragraph-m" bold>
                      Nature elements for Schleswig Holstein and Hamburg should be updated by submitting a ticket through the usual sprint workflow. This is due
                      to some complex logic (does not contain 'CODE' or 'NUTZ_CODE', and has no 'NAME') which does not work with our current system.
                    </Typography>
                  ) : (
                    natureElement?.map((item, i) => (
                      <TagNormal
                        text={
                          <>
                            {item.property} <Switch defaultChecked={item.contains} onChange={() => handleNatureElementSwitch(i)} className="margin-left-50" />
                            <Icon iconName="tabler/x" onClick={() => removeItem(i, "natureElement")} className="pointer icon inline" />
                          </>
                        }
                        key={i}
                        style={{ marginBottom: "0.1rem" }}
                        color={item.contains ? "green" : "red"}
                      />
                    ))
                  )}
                </Col>
              }
            </Row>

            <AdminSection
              headline="Crop"
              description={
                <>
                  If any of the following parameters are found in the shape file, it will be assigned to the field number. This is not currently relevant to our
                  farmers, however we are using this data to try and build a remote sensing solution.
                </>
              }
            />
            <Row>
              <Col md={10}>
                <Input allowClear label="Add a property" onPressEnter={(v) => addItem(v, "crop")} />
              </Col>
              <Col md={14}>
                {crop?.map((item, i) => (
                  <TagNormal
                    key={i}
                    text={
                      <>
                        {item} <Icon iconName="tabler/x" className="pointer icon inline" onClick={() => removeItem(i, "crop")} />
                      </>
                    }
                  />
                ))}
              </Col>
            </Row>
            <Row>
              <Col sm={24} md={24} className="center">
                <Button type="primary" onClick={() => submitChanges()} className="margin-top-200">
                  Save all changes
                </Button>
              </Col>
            </Row>
          </>
        ) : null}
      </ErrorBoundary>
    </MainPage>
  );
};
